import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";

// redux
import { useSelector } from "react-redux";

//import components
import ConsumerFilter from "../../common/ConsumerFilter";
import WeightToggleIcon from "../../../assets/icons/weightToggle.png";
import HeaderSampleSize from "./HeaderSampleSize";
import { getRecallAndITM, getConceptAndRecall, getProductScoreWithNaAndRowSpan, getScoreByVariable, getProductColumnClassName, getViewKeys, getOralCareProductCodeTableHeader, getFormattedScoreOrNA, isConceptVariable } from "../../../utils/analysisTable/analysisUtils";
import { getColorsAndLetter, getTestResultByVariable } from "../../../utils/analysisTable/tTestutils";
import TableCellWithOuterSeparator from "./TableCellWithOuterSeparator";

function DragRenderTable({ dragSettings, analysisView, productData, tableData, textThemeData = {} }) {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const selectedDataset = useSelector((state) => state.analysisResultsStore.selectedDataset);
    const productsTestCodeMapData = useSelector((state) => state.studyDataStore.productTestCodeMap);
    const productColumnVisibility = useSelector((state) => state.productsInfoStore.productsVisibility);
    const testAnalysisType = useSelector((state) => state.tTestStore.testAnalysisType);

    const { draggableColumns, draggedColumnIndex, mousePosition, draggedColumnDiamension, tableHeadSpan, draggedElementRef } = dragSettings;
    const { box, isITMDataShown, moduleName, analysisTypeValue, boxAnalysisTypeSelection, penaltyToggle = false } = analysisView;
    const { noOfProducts } = productData;
    const { tableDataMapping, isConsumerFilterLoadingStoreData, removedKpiStoreState, dataViewKey, tTestResult } = tableData;
    const {
        hasITMTextTheme = false,
        hasRecallTextTheme = false,
        hasStandardTextTheme = false,
        enableTextThemeAnalysis = false,
        TTIconWithTooltip = null,
        textThemeHeaderStyles = {},
        checkedTTBoxes = {}
    } = textThemeData;

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellbeing = moduleName === "justRight" && (isHairCare || isSkinCare);
    const ttExtraSpan = isITMDataShown
        ? (hasITMTextTheme ? 1 : 0) + (hasRecallTextTheme ? 1 : 0)
        : (hasStandardTextTheme ? 1 : 0);

    const totalColSpan = tableHeadSpan + ttExtraSpan;

    const isConceptAndRecallFeature = isHairCare && moduleName !== "claims" && moduleName !== "fragranceMetrics" && moduleName !== "justRight";
    const showConceptData = isConceptAndRecallFeature && selectedDataset?.value === "concept";
    const showRecallData = isConceptAndRecallFeature && selectedDataset?.value === "recall";
    const showConceptAndRecallData = isConceptAndRecallFeature && selectedDataset?.value === "concept_recall";
    const isConceptRecallShown = isHairCare && (showConceptData || showRecallData || showConceptAndRecallData);

    const dragReturnTableHeader = () => {
        const datum = draggableColumns?.[draggedColumnIndex];
        const productCodeCoreDataId = datum.coreDataId;
        let productCodeTitle = datum.title;
        let studyReferenceOrTestCode = selectedStudy?.find((study) => study.id === productCodeCoreDataId)?.study_reference__title;
        const showWeightIcon = selectedStudy.length > 1 && selectedStudy?.find((study) => study.id === datum.coreDataId)?.weighted === true;
        if (isOralCare) {
            const [title, code] = getOralCareProductCodeTableHeader(datum, productsTestCodeMapData);
            productCodeTitle = title;
            studyReferenceOrTestCode = (
                <>
                    {code} / {studyReferenceOrTestCode}
                </>
            );
        }
        return (
            <tr>
                <TableCellWithOuterSeparator
                    value={
                        <div
                            style={{
                                textAlign: "center",
                            }}
                        >
                            {showWeightIcon && (
                                <img
                                    alt="weightToggle"
                                    style={{
                                        width: 14,
                                        marginRight: 10,
                                    }}
                                    src={WeightToggleIcon}
                                />
                            )}
                            <button
                                className="clean-button color-blue"
                                style={{
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                }}
                            >
                                {productCodeTitle} / {studyReferenceOrTestCode}
                            </button>

                            <HeaderSampleSize productData={datum} />
                        </div>
                    }
                    cellType="th"
                    testLabel=""
                    mainProps={{
                        colSpan: totalColSpan,
                        align: "center",
                    }}
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "move",
                            minWidth: 200,
                            maxWidth: 300,
                        },
                        wrapper: {
                            justifyContent: "space-between",
                        },
                        value: {
                            justifyContent: "space-between",
                            flexDirection: "column",
                            alignItems: "center",
                        },
                    }}
                />
            </tr>
        );
    };
    const dragReturnConsumerFilterRow = () => {
        const datum = draggableColumns?.[draggedColumnIndex];
        return (
            <tr>
                <td
                    colSpan={totalColSpan}
                    style={{
                        textAlign: "center",
                        fontWeight: "500",
                        padding: "6px 0px",
                    }}
                    key={datum.coreDataId + 1 + "drag-cf"}
                >
                    {!isConsumerFilterLoadingStoreData ? (
                        <ConsumerFilter
                            productsSecondaryData={draggableColumns}
                            product={datum}
                        />
                    ) : (
                        <CircularProgress size={20} />
                    )}
                </td>
            </tr>
        );
    };

    const dragReturnItmRecallLabelRow = () => {
        return (
            <tr>
                {/* ITM TT Icon Column */}
                {hasITMTextTheme && (
                    <TableCellWithOuterSeparator
                        value={TTIconWithTooltip}
                        cellType="td"
                        testLabel=""
                        mainProps={{ colSpan: 1 }}
                        styles={textThemeHeaderStyles}
                        showSeperator={true}
                    />
                )}
                {/* ITM Label */}
                <TableCellWithOuterSeparator
                    value={"ITM"}
                    cellType="td"
                    testLabel=""
                    mainProps={{
                        colSpan: isITMDataShown && !box ? 1 : dataViewKey.split(",").length,
                    }}
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                            minWidth: 120,
                            maxWidth: 200,
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
                {/* Recall TT Icon Column */}
                {hasRecallTextTheme && (
                    <TableCellWithOuterSeparator
                        value={TTIconWithTooltip}
                        cellType="td"
                        testLabel=""
                        mainProps={{ colSpan: 1 }}
                        styles={textThemeHeaderStyles}
                        showSeperator={true}
                    />
                )}
                {/* Recall Label */}
                <TableCellWithOuterSeparator
                    value={"Recall"}
                    cellType="td"
                    testLabel=""
                    mainProps={{
                        colSpan: dataViewKey.split(",").length,
                    }}
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                            minWidth: 120,
                            maxWidth: 200,
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
            </tr>
        );
    };

    const dragReturnConceptRecallLabelRow = () => {
        return (
            <tr>
                <TableCellWithOuterSeparator
                    value={"Concept"}
                    cellType="td"
                    testLabel=""
                    mainProps={{
                        colSpan: dataViewKey.split(",").length,
                    }}
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
                <TableCellWithOuterSeparator
                    value={"Recall"}
                    cellType="td"
                    testLabel=""
                    mainProps={{
                        colSpan: dataViewKey.split(",").length,
                    }}
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
            </tr>
        );
    };

    const dragReturnBoxAnalysisLabelRow = () => {
        return (
            <tr>
                {isITMDataShown &&
                    !showConceptAndRecallData &&
                    boxAnalysisTypeSelection.map((ele, i) => {
                        const key = "box-analysis-itm-drag-product-label-column-" + i;
                        return (
                            <TableCellWithOuterSeparator
                                key={key}
                                value={ele}
                                cellType="td"
                                testLabel=""
                                styles={{
                                    main: {
                                        fontWeight: "600",
                                        cursor: "pointer",
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele === "Bottom Box" ? false : true}
                            />
                        );
                    })}
                {showConceptAndRecallData
                    ? ["Concept", "Recall"].map((type) =>
                        boxAnalysisTypeSelection.map((ele, i) => {
                            const key = `box-analysis-${type.toLowerCase()}-drag-product-label-column-${i}`;
                            return (
                                <TableCellWithOuterSeparator
                                    key={key}
                                    value={ele}
                                    cellType="td"
                                    testLabel=""
                                    styles={{
                                        main: {
                                            fontWeight: "600",
                                            cursor: "pointer",
                                        },
                                        wrapper: {},
                                        value: {
                                            justifyContent: "center",
                                            color: "#005eef",
                                            background: "#f0f8ff",
                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                            fontWeight: "600",
                                            borderRadius: "5px",
                                            padding: "5px",
                                        },
                                    }}
                                    showSeperator={type === "Concept" ? (ele === "Bottom Box" ? false : true) : false}
                                />
                            );
                        })
                    )
                    : boxAnalysisTypeSelection.map((ele, i) => {
                        const key = "box-analysis-recall-drag-product-label-column-" + i;
                        return (
                            <TableCellWithOuterSeparator
                                key={key}
                                value={ele}
                                cellType="td"
                                testLabel=""
                                styles={{
                                    main: {
                                        fontWeight: "600",
                                        cursor: "pointer",
                                    },
                                    wrapper: {},
                                    value: {
                                        justifyContent: "center",
                                        color: "#005eef",
                                        background: "#f0f8ff",
                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                        fontWeight: "600",
                                        borderRadius: "5px",
                                        padding: "5px",
                                    },
                                }}
                                showSeperator={ele === "Bottom Box" ? false : isITMDataShown ? false : true}
                            />
                        );
                    })}
            </tr>
        );
    };

    const dragReturnJarBoxAnalysisLabelRow = () => {
        return (
            <tr>
                {penaltyToggle && (
                    <TableCellWithOuterSeparator
                        value="Penalty"
                        cellType="td"
                        testLabel=""
                        styles={{
                            main: {
                                fontWeight: "600",
                                cursor: "pointer",
                            },
                            wrapper: {},
                            value: {
                                justifyContent: "center",
                                color: "#005eef",
                                background: "#f0f8ff",
                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                fontWeight: "600",
                                borderRadius: "5px",
                                padding: "5px",
                            },
                        }}
                        showSeperator={true}
                    />
                )}
                <TableCellWithOuterSeparator
                    value={"Too Little"}
                    cellType="td"
                    testLabel=""
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={true}
                />
                <TableCellWithOuterSeparator
                    value={"Just Right"}
                    cellType="td"
                    testLabel=""
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
                <TableCellWithOuterSeparator
                    value={"Too Much"}
                    cellType="td"
                    testLabel=""
                    styles={{
                        main: {
                            fontWeight: "600",
                            cursor: "pointer",
                        },
                        wrapper: {},
                        value: {
                            justifyContent: "center",
                            color: "#005eef",
                            background: "#f0f8ff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            fontWeight: "600",
                            borderRadius: "5px",
                            padding: "5px",
                        },
                    }}
                    showSeperator={false}
                />
                {penaltyToggle && (
                    <TableCellWithOuterSeparator
                        value="Penalty"
                        cellType="td"
                        testLabel=""
                        styles={{
                            main: {
                                fontWeight: "600",
                                cursor: "pointer",
                            },
                            wrapper: {},
                            value: {
                                justifyContent: "center",
                                color: "#005eef",
                                background: "#f0f8ff",
                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                fontWeight: "600",
                                borderRadius: "5px",
                                padding: "5px",
                            },
                        }}
                        showSeperator={false}
                    />
                )}
            </tr>
        );
    };
    const dragReturnTextThemeLabelRow = () => {
        return (
            <tr>
                {/* TT Icon Header */}
                {hasStandardTextTheme && (
                    <TableCellWithOuterSeparator
                        value={TTIconWithTooltip}
                        cellType="td"
                        testLabel=""
                        mainProps={{ colSpan: 1 }}
                        styles={textThemeHeaderStyles}
                        showSeperator={true}
                    />
                )}
                {/* Empty cell for score column */}
                <TableCellWithOuterSeparator
                    value={""}
                    cellType="td"
                    testLabel=""
                    mainProps={{ colSpan: dataViewKey.split(",").length }}
                    styles={{
                        main: {},
                        wrapper: {},
                        value: {
                            background: "transparent",
                            border: "none",
                            height: "32px"
                        }
                    }}
                    showSeperator={true}
                />
            </tr>
        );
    };

    const getCheckboxFromAPI = (datum, type, itmBvObj, recallBvObj, filteredBvObjScoreForTT) => {
        if (!enableTextThemeAnalysis) return false;
        let backendVar = "";
        if (type === "itm" && itmBvObj?.backendVariable) {
            backendVar = itmBvObj.backendVariable;
        } else if (type === "recall" && recallBvObj?.backendVariable) {
            backendVar = recallBvObj.backendVariable;
        } else if (type === "std" && filteredBvObjScoreForTT?.backendVariable) {
            backendVar = filteredBvObjScoreForTT.backendVariable;
        }
        if (!backendVar) return false;
        const scoreObj = datum.scores?.find(s => s.backendVariable === backendVar);
        return scoreObj?.text_theme_check_box === true;
    };

    const renderDragTTCell = (datum, index, type, itmBvObj, recallBvObj, filteredBvObjScoreForTT, className) => {
        const shouldShow = getCheckboxFromAPI(datum, type, itmBvObj, recallBvObj, filteredBvObjScoreForTT);

        const productCoreDataId = datum?.coreDataId || index;
        const productTitle = datum?.title?.replace(/\s+/g, "_") || index;
        const backendVarForKey = type === "itm"
            ? (itmBvObj?.backendVariable || "itm")
            : type === "recall"
                ? (recallBvObj?.backendVariable || "recall")
                : (filteredBvObjScoreForTT?.backendVariable || "std");

        const checkboxKey = `${productCoreDataId}-${productTitle}-${backendVarForKey}`;
        const isChecked = checkedTTBoxes[checkboxKey] || false;

        return (
            <TableCellWithOuterSeparator
                key={`drag-tt-${type}-${index}`}
                value={
                    shouldShow ? (
                        <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={() => { }}
                            sx={{
                                padding: 0,
                                color: "#d0d5dd",
                                "&.Mui-checked": { color: "#005eef" },
                                "& .MuiSvgIcon-root": { fontSize: 16 },
                            }}
                        />
                    ) : (
                        <div style={{ width: "16px", height: "16px" }} />
                    )
                }
                cellType="td"
                testLabel=""
                mainProps={{ className }}
                styles={{
                    main: {
                        minWidth: 36,
                        maxWidth: 36,
                        width: 36,
                        padding: "4px 2px",
                    },
                    wrapper: {
                        justifyContent: "center",
                        alignItems: "center",
                        padding: 0,
                        height: "100%",
                        width: "100%",
                        display: "flex",
                    },
                    value: {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background: "#fff",
                        border: "1px solid #ffffff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.36)",
                        borderRadius: "5px",
                        width: "26px",
                        height: "26px",
                    }
                }}
                showSeperator={true}
            />
        );
    };

    const dragReturnAnalysisScoresRow = () => {
        const [key1, key2, key3] = getViewKeys(dataViewKey);
        const conceptCategoryByModule = {
            overall: "Concept KPI (OO)",
            agreement: "Concept Agreement KPI",
            secondary: "Concept Secondary Attribute",
        };
        const recallCategoryByModule = {
            overall: "KPI (OO)",
            agreement: "Agreement KPI",
            secondary: "Secondary Attribute",
        };
        const allMappingsBvs = showConceptAndRecallData
            ? tableDataMapping.flatMap((m) => m.displayLabels || [])
            : [];
        const _roww = tableDataMapping.map((catObj, i) => {
            const allKpis = catObj?.displayLabels || [];
            let selectedKpis = allKpis;
            if (showConceptData && conceptCategoryByModule[moduleName]) {
                selectedKpis = allKpis.filter((kpi) => kpi.variableCategory === conceptCategoryByModule[moduleName]);
            } else if (showRecallData && recallCategoryByModule[moduleName]) {
                selectedKpis = allKpis.filter((kpi) => kpi.variableCategory === recallCategoryByModule[moduleName]);
            }
            const tableCatObj = {
                ...catObj,
                displayLabels: selectedKpis,
            };
            const scoreCatObj = isHairCare && showConceptAndRecallData ? catObj : tableCatObj;
            const kpis = tableCatObj.displayLabels || [];
            const { productScoreWithNaObj } = getProductScoreWithNaAndRowSpan({ catObj: scoreCatObj, box, key1, key2, key3, isITMDataShown, moduleName, draggableColumns, noOfProducts, removedKpiStoreState, isConceptRecallShown, isHairCare, isConceptDataShown: showConceptData });
            const uniqueKpis = new Set();

            const _trs = kpis
                .map((bvObj) => {
                    const rowDisplayLabel = bvObj.displayLabel;
                    const rowBackendVariable = bvObj.backendVariable;
                    if (!uniqueKpis.has(bvObj.displayLabel)) {
                        const { recallBvObj, itmBvObj } = getRecallAndITM(rowDisplayLabel, rowBackendVariable, moduleName, kpis);
                        const productsScoreWithNA = productScoreWithNaObj[rowDisplayLabel];
                        const filteredBvObjScoreForTT = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};

                        const row = (
                            <tr key={i + 1 + "drag-row"}>
                                {draggableColumns.map((datum, index) => {
                                    // const key = index + 1;

                                    // let recallScore = null;
                                    // let itmBvScore = null;

                                    // if (moduleName === "overall") {
                                    //     recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === recallBvObj?.backendVariable)?.[0] || {};
                                    //     if (isITMDataShown) {
                                    //         itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                                    //     }
                                    // } else {
                                    //     const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                                    //     recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === filteredBvObjScore?.backendVariable)?.[0] || {};
                                    // }

                                    let recallScore = null;
                                    let itmBvScore = null;
                                    let conceptScore = null;
                                    let recallTest = null;
                                    let itmTest = null;
                                    let conceptTest = null;
                                    const productCodeCoreDataId = datum.coreDataId;

                                    if (moduleName === "overall") {
                                        //recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === recallBvObj?.backendVariable)?.[0] || {};
                                        recallScore = getScoreByVariable(datum.scores, recallBvObj?.backendVariable);
                                        recallTest = getTestResultByVariable(tTestResult, recallBvObj?.backendVariable, productCodeCoreDataId);

                                        if (isITMDataShown) {
                                            //itmBvScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === itmBvObj?.backendVariable)?.[0] || {};
                                            itmBvScore = getScoreByVariable(datum.scores, itmBvObj?.backendVariable);
                                            itmTest = getTestResultByVariable(tTestResult, itmBvObj?.backendVariable, productCodeCoreDataId);
                                        }
                                    } else {
                                        const filteredBvObjScore = kpis.filter((ele) => ele.displayLabel === rowDisplayLabel)?.[0] || {};
                                        // recallScore = datum.scores.filter((scoreObj) => scoreObj.backendVariable === filteredBvObjScore?.backendVariable)?.[0] || {};
                                        recallScore = getScoreByVariable(datum.scores, filteredBvObjScore?.backendVariable);
                                        recallTest = getTestResultByVariable(tTestResult, filteredBvObjScore?.backendVariable, productCodeCoreDataId);
                                    }

                                    // ==================== CONCEPT/RECALL: resolve concept + recall scores ====================
                                    if (isConceptRecallShown && showConceptData) {
                                        // concept-only: look up the Concept BV directly
                                        const { conceptBvObj } = getConceptAndRecall(rowDisplayLabel, rowBackendVariable, moduleName, kpis);
                                        conceptScore = getScoreByVariable(datum.scores, conceptBvObj?.backendVariable);
                                        conceptTest = getTestResultByVariable(tTestResult, conceptBvObj?.backendVariable, productCodeCoreDataId);
                                    } else if (isConceptRecallShown) {
                                        const { conceptBvObj, recallBvObj: conceptRecallBvObj } = getConceptAndRecall(
                                            rowDisplayLabel,
                                            rowBackendVariable,
                                            moduleName,
                                            kpis
                                        );
                                        conceptScore = getScoreByVariable(datum.scores, conceptBvObj?.backendVariable);
                                        recallScore = getScoreByVariable(datum.scores, conceptRecallBvObj?.backendVariable);
                                        conceptTest = getTestResultByVariable(tTestResult, conceptBvObj?.backendVariable, productCodeCoreDataId);
                                        recallTest = getTestResultByVariable(tTestResult, conceptRecallBvObj?.backendVariable, productCodeCoreDataId);
                                    }

                                    const productCodeTitle = datum.title;
                                    const className = getProductColumnClassName(productCodeTitle, productCodeCoreDataId, productColumnVisibility);
                                    const recallTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, recallTest);
                                    const conceptTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, conceptTest);
                                    const itmTestResult = getColorsAndLetter(testAnalysisType, productCodeTitle, itmTest);

                                    const leftPenalty = isSkinCare ? Math.abs(parseFloat(recallScore?.bottom_penalty)) : parseFloat(recallScore?.bottom_penalty);
                                    const rightPenalty = isSkinCare ? Math.abs(parseFloat(recallScore?.top_penalty)) : parseFloat(recallScore?.top_penalty);
                                    const jrPctDL = parseFloat(recallScore?.[dataViewKey.split(",")[1]]) || 0;
                                    const isBlackStyle = jrPctDL >= 75;
                                    const getPenaltyColor = (p) => {
                                        if (jrPctDL >= 75) return "#000";
                                        if (p >= 0.75) return "#E14370";
                                        if (p >= 0.65) return "#D264F4";
                                        return "#31A7FA";
                                    };

                                    // ==================== CONCEPT DATA ONLY ====================
                                    if (showConceptData) {
                                        const _tdConcept = (
                                            <>
                                                {dataViewKey.split(",").map((ele, k) => {
                                                    const key = "concept-only-drag-value-column-" + index + "-" + k;
                                                    const showTestLabel = conceptTest?.test_name !== "student's t test comparisons" && conceptTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                    const testLabel = conceptTestResult?.["label"] ?? null;
                                                    const isKruskallWallis = isHairCare && conceptTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                    const scoreShow = conceptScore?.[dataViewKey.split(",")[k]];
                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={key}
                                                            value={getFormattedScoreOrNA(scoreShow, analysisTypeValue?.value)}
                                                            cellType="td"
                                                            testLabel={showTestLabel ? testLabel : ""}
                                                            disableColor={isKruskallWallis}
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {},
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: conceptTestResult?.["color"]?.[ele] ?? "#fff",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                },
                                                            }}
                                                            showSeperator={ele.startsWith("bottom") ? false : true}
                                                        />
                                                    );
                                                })}
                                            </>
                                        );
                                        return draggedColumnIndex === index ? _tdConcept : null;
                                    }

                                    // ==================== CONCEPT + RECALL DATA ====================
                                    if (showConceptAndRecallData) {
                                        const _tdConceptRecall = (
                                            <>
                                                {dataViewKey.split(",").map((ele, k) => {
                                                    const key = "concept-recall-drag-concept-value-column-" + index + "-" + k;
                                                    const showTestLabel = conceptTest?.test_name !== "student's t test comparisons" && conceptTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                    const testLabel = conceptTestResult?.["label"] ?? null;
                                                    const isKruskallWallis = isHairCare && conceptTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                    const scoreShow = conceptScore?.[dataViewKey.split(",")[k]];
                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={key}
                                                            value={getFormattedScoreOrNA(scoreShow, analysisTypeValue?.value)}
                                                            cellType="td"
                                                            testLabel={showTestLabel ? testLabel : ""}
                                                            disableColor={isKruskallWallis}
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {},
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: conceptTestResult?.["color"]?.[ele] ?? "#fff",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                },
                                                            }}
                                                            showSeperator={ele.startsWith("bottom") ? false : true}
                                                        />
                                                    );
                                                })}
                                                {dataViewKey.split(",").map((ele, k) => {
                                                    const key = "concept-recall-drag-recall-value-column-" + index + "-" + k;
                                                    const showTestLabel = recallTest?.test_name !== "student's t test comparisons" && recallTest?.test_name !== "student's t test comparisons(1-tailed)";
                                                    const testLabel = recallTestResult?.["label"] ?? null;
                                                    const isKruskallWallis = isHairCare && recallTest?.test_name?.toLowerCase().includes("kruskall wallis");
                                                    const scoreShow = recallScore?.[dataViewKey.split(",")[k]];
                                                    return (
                                                        <TableCellWithOuterSeparator
                                                            key={key}
                                                            value={getFormattedScoreOrNA(scoreShow, analysisTypeValue?.value)}
                                                            cellType="td"
                                                            testLabel={showTestLabel ? testLabel : ""}
                                                            disableColor={isKruskallWallis}
                                                            mainProps={{
                                                                className: className,
                                                            }}
                                                            styles={{
                                                                main: {},
                                                                wrapper: {},
                                                                value: {
                                                                    justifyContent: "center",
                                                                    background: recallTestResult?.["color"]?.[ele] ?? "#fff",
                                                                    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                    borderRadius: "5px",
                                                                    padding: "5px",
                                                                },
                                                            }}
                                                            showSeperator={false}
                                                        />
                                                    );
                                                })}
                                            </>
                                        );
                                        return draggedColumnIndex === index ? _tdConceptRecall : null;
                                    }
                                    const _td = (
                                        <>
                                            {/* ITM TT checkbox + ITM scores */}
                                            {isITMDataShown && (
                                                <>
                                                    {hasITMTextTheme && renderDragTTCell(datum, index, "itm", itmBvObj, recallBvObj, filteredBvObjScoreForTT, className)}
                                                    {dataViewKey.split(",").map((ele, k) => {
                                                        const key = "box-analysis-drag-itm-value-column-" + index + "-" + k;
                                                        return (
                                                            <TableCellWithOuterSeparator
                                                                key={key}
                                                                value={getFormattedScoreOrNA(itmBvScore[dataViewKey.split(",")[k]], analysisTypeValue?.value)}
                                                                cellType="td"
                                                                testLabel={itmTestResult?.["label"] ?? null}
                                                                mainProps={{
                                                                    className: className,
                                                                }}
                                                                styles={{
                                                                    main: {},
                                                                    wrapper: {},
                                                                    value: {
                                                                        justifyContent: "center",
                                                                        background: itmTestResult?.["color"]?.[ele] ?? "#fff",
                                                                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                        borderRadius: "5px",
                                                                        padding: "5px",
                                                                    },
                                                                }}
                                                                showSeperator={ele.startsWith("bottom") ? false : true}
                                                            />
                                                        );
                                                    })}
                                                    {/* Recall TT checkbox */}
                                                    {hasRecallTextTheme && renderDragTTCell(datum, index, "recall", itmBvObj, recallBvObj, filteredBvObjScoreForTT, className)}
                                                </>
                                            )}

                                            {/* Standard TT checkbox (non-ITM pages) */}
                                            {!isITMDataShown && hasStandardTextTheme && renderDragTTCell(datum, index, "std", itmBvObj, recallBvObj, filteredBvObjScoreForTT, className)}

                                            {isBeautyAndWellbeing && penaltyToggle && (
                                                <TableCellWithOuterSeparator
                                                    key={`drag-penalty-left-${index}`}
                                                    value={recallScore?.bottom_penalty !== null && recallScore?.bottom_penalty !== undefined && recallScore?.bottom_penalty !== "NaN" && !isNaN(Number(recallScore?.bottom_penalty)) ? Number(leftPenalty).toFixed(2) : "NA"}
                                                    cellType="td"
                                                    testLabel=""
                                                    mainProps={{ className }}
                                                    styles={{
                                                        main: {},
                                                        wrapper: {},
                                                        value: {
                                                            justifyContent: "center",
                                                            background: "#fff",
                                                            color: getPenaltyColor(leftPenalty),
                                                            border: isBeautyAndWellbeing && !isBlackStyle ? `1px solid ${getPenaltyColor(leftPenalty)}` : "none",
                                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                            borderRadius: "5px",
                                                            padding: "5px",
                                                            fontWeight: isBeautyAndWellbeing && !isBlackStyle ? "600" : "normal",
                                                        },
                                                    }}
                                                    showSeperator={true}
                                                />
                                            )}

                                            {/* Recall scores */}
                                            {dataViewKey.split(",").map((ele, k) => {
                                                const key = "box-analysis-drag-recall-value-column-" + index + "-" + k;
                                                const jrPct = parseFloat(recallScore?.[dataViewKey.split(",")[1]]) || 0;
                                                const tlPct = parseFloat(recallScore?.[dataViewKey.split(",")[0]]) || 0;
                                                const tmPct = parseFloat(recallScore?.[dataViewKey.split(",")[2]]) || 0;
                                                const diff = Math.abs(tlPct - tmPct);
                                                const jarBg = (() => {
                                                    if (isBeautyAndWellbeing && k === 1) {
                                                        return jrPct >= 75 ? "#008000" : "#FFBF00";
                                                    }
                                                    if (!penaltyToggle) return recallTestResult?.["color"]?.[ele] ?? "#fff";
                                                    if (jrPct >= 75) {
                                                        if (k === 1) return "#008000";
                                                        return "#fff";
                                                    }
                                                    if (k === 1) return "#FFBF00";
                                                    if (k === 0 && tlPct >= 20) return "#FF0000";
                                                    if (k === 2 && tmPct >= 20) return "#FF0000";
                                                    return diff >= 10 ? "#FF0000" : "#808080";
                                                })();
                                                return (
                                                    <TableCellWithOuterSeparator
                                                        key={key}
                                                        value={getFormattedScoreOrNA(recallScore[dataViewKey.split(",")[k]], analysisTypeValue?.value)}
                                                        cellType="td"
                                                        testLabel={recallTestResult?.["label"] ?? null}
                                                        mainProps={{
                                                            className: className,
                                                        }}
                                                        styles={{
                                                            main: {},
                                                            wrapper: {},
                                                            value: {
                                                                justifyContent: "center",
                                                                background: jarBg,
                                                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                                borderRadius: "5px",
                                                                padding: "5px",
                                                            },
                                                        }}
                                                        showSeperator={isBeautyAndWellbeing ? (penaltyToggle ? false : k === 0) : (ele.startsWith("bottom") ? false : isITMDataShown ? false : true)}
                                                    />
                                                );
                                            })}
                                            {isBeautyAndWellbeing && penaltyToggle && (
                                                <TableCellWithOuterSeparator
                                                    key={`drag-penalty-right-${index}`}
                                                    value={recallScore?.top_penalty !== null && recallScore?.top_penalty !== undefined && recallScore?.top_penalty !== "NaN" && !isNaN(Number(recallScore?.top_penalty)) ? Number(rightPenalty).toFixed(2) : "NA"}
                                                    cellType="td"
                                                    testLabel=""
                                                    mainProps={{ className }}
                                                    styles={{
                                                        main: {},
                                                        wrapper: {},
                                                        value: {
                                                            justifyContent: "center",
                                                            background: "#fff",
                                                            color: getPenaltyColor(rightPenalty),
                                                            border: isBeautyAndWellbeing && !isBlackStyle ? `1px solid ${getPenaltyColor(rightPenalty)}` : "none",
                                                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                                            borderRadius: "5px",
                                                            padding: "5px",
                                                            fontWeight: isBeautyAndWellbeing && !isBlackStyle ? "600" : "normal",
                                                        },
                                                    }}
                                                    showSeperator={false}
                                                />
                                            )}
                                        </>
                                    );
                                    return draggedColumnIndex === index ? _td : null;
                                })}
                            </tr>
                        );

                        const isConceptBv = isConceptVariable(rowBackendVariable, bvObj.variableCategory);
                        const siblingSearchKpis = showConceptAndRecallData ? allMappingsBvs : kpis;
                        const hasSiblingBv = isHairCare && siblingSearchKpis.some((ele) => ele.displayLabel === bvObj.displayLabel && ele.backendVariable !== rowBackendVariable);
                        const bvHasValues = (bv) =>
                            draggableColumns?.some((datum) => {
                                const s = getScoreByVariable(datum.scores, bv);
                                if (!s?.backendVariable) return false;
                                return box ? !!(s[key1] || s[key2] || (key3 && s[key3])) : !!s[key1];
                            });
                        const thisBvHasData = isHairCare && bvHasValues(rowBackendVariable);
                        const siblingHasData = isHairCare && siblingSearchKpis.some((ele) => ele.displayLabel === bvObj.displayLabel && ele.backendVariable !== rowBackendVariable && bvHasValues(ele.backendVariable));
                        const shouldHideForConceptRecall = hasSiblingBv && ((showConceptData && !isConceptBv) || (showRecallData && isConceptBv) || (showConceptAndRecallData && isConceptBv && siblingHasData));
                        const siblingSharesLabelWithData = !shouldHideForConceptRecall && isHairCare && siblingHasData;
                        if (shouldHideForConceptRecall || (siblingSharesLabelWithData && !thisBvHasData)) {
                            return null;
                        }
                        // If this BV has data, never hide it due to NA count (concept BVs with data must show)
                        if (productsScoreWithNA === noOfProducts && !thisBvHasData) {
                            return null;
                        } else {
                            uniqueKpis.add(bvObj.displayLabel);
                            if (moduleName !== "claims" && removedKpiStoreState.flatMap((item) => item.split(",")).includes(bvObj.backendVariable)) {
                                return;
                            } else if (moduleName === "claims" && removedKpiStoreState.includes(bvObj.backendVariable)) {
                                return;
                            }
                            return row;
                        }
                    } else {
                        return;
                    }
                })
                .filter((ele) => ele);
            return _trs;
        });
        return _roww;
    };

    return (
        <div
            ref={draggedElementRef}
            style={{
                position: "fixed",
                zIndex: 1000,
                pointerEvents: "none",
                opacity: 1,
                top: mousePosition.y,
                left: mousePosition.x,
                width: draggedColumnDiamension.width,
                minHeight: draggedColumnDiamension.height,
            }}
        >
            <table
                className="table text-sm analysis-table"
                style={{
                    background: "white",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    width: "100%",
                    height: draggedColumnDiamension.height,
                }}
            >
                <thead>{dragReturnTableHeader()}</thead>
                <tbody>
                    {dragReturnConsumerFilterRow()}
                    {isITMDataShown && !showConceptAndRecallData && dragReturnItmRecallLabelRow()}
                    {showConceptAndRecallData && dragReturnConceptRecallLabelRow()}
                    {box && dragReturnBoxAnalysisLabelRow()}
                    {isBeautyAndWellbeing && !showConceptAndRecallData && dragReturnJarBoxAnalysisLabelRow()}
                    {isBeautyAndWellbeing && dragReturnJarBoxAnalysisLabelRow()}
                    {!isITMDataShown && !box && !isBeautyAndWellbeing && hasStandardTextTheme && dragReturnTextThemeLabelRow()}
                    {dragReturnAnalysisScoresRow()}
                </tbody>
            </table>
        </div>
    );
}

DragRenderTable.propTypes = {
    dragSettings: PropTypes.object,
    analysisView: PropTypes.object,
    productData: PropTypes.object,
    tableData: PropTypes.object,
    textThemeData: PropTypes.object,
};
export default DragRenderTable;
